import { SegmentLoader } from './SegmentLoader.es.js';

var TransformMethods;
(function (TransformMethods) {
    TransformMethods[TransformMethods["JOIN"] = 0] = "JOIN";
    TransformMethods[TransformMethods["UPPERCASE"] = 1] = "UPPERCASE";
    TransformMethods[TransformMethods["LOWERCASE"] = 2] = "LOWERCASE";
})(TransformMethods || (TransformMethods = {}));
const userPropertiesMapping = {
    UserId: 'L - User Id',
    UserFirstName: 'L - User First Name',
    UserLastName: 'L - User Last Name',
    UserEmail: 'L - User Email',
    UserLanguage: 'L - User Language',
};
function getUserProperties(user) {
    return Object.entries(user).reduce((acc, [key, value]) => {
        if (Object.prototype.hasOwnProperty.call(userPropertiesMapping, key)) {
            acc[userPropertiesMapping[key]] = value;
        }
        return acc;
    }, {});
}
/**
 * This abstract service will be extended from the SegmentService that gets generated
 * by running the `setup-analytics` command.
 *
 * @export
 * @class AbstractSegmentService
 */
class AbstractSegmentService {
    constructor(config, windowObject) {
        this.disabled = false;
        this.segmentWriteKey = config.segmentWriteKey;
        this.window = windowObject;
        this.segmentLoader = new SegmentLoader(windowObject);
        if (typeof this.window === 'undefined') {
            this.disabled = true;
        }
    }
    /**
     * Load analytics
     *
     * Analytics might never load on browsers with Ad Blockers.
     * To prevent returing a promise that never resolves, this method will resolve after a timeout.
     *
     * Hence there are 3 scenarios:
     *  1. (Happy path) - Segment has finished loading in time, yay!
     *  2. (Ad Blockers) - Segment will never load
     *     ...this method will resolve after after a timeout
     *  3. (Slow Connexions) - 6 seconds have passed and this method has resolved, but Segment is still loading
     *     ... this is not a problem
     *     ... future events will be put in the queue until Loading is truly finished
     *     ... 6 seconds is what it takes chrome to load the analytics on a Slow 3G connexion.
     *
     * @returns A promise that resolve when analytics are loaded, to a max of wait time 6 seconds.
     */
    load() {
        return this.segmentLoader.load(this.segmentWriteKey);
    }
    identify(user, applicationProperties) {
        if (this.disabled || !this.window.analytics) {
            return;
        }
        this.window.analytics.ready(() => {
            const analyticsUser = this.window.analytics.user && this.window.analytics.user();
            const analyticsGroup = this.window.analytics.group && this.window.analytics.group();
            if (analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.traits) {
                analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.traits({});
            }
            if (analyticsGroup === null || analyticsGroup === void 0 ? void 0 : analyticsGroup.traits) {
                analyticsGroup === null || analyticsGroup === void 0 ? void 0 : analyticsGroup.traits({});
            }
            if (user) {
                this.window.analytics.identify(user.UserId, Object.assign(Object.assign({}, getUserProperties(user)), applicationProperties));
                // Links anonymous identify for Chameleon to the identified user
                if (analyticsUser && analyticsUser.anonymousId) {
                    this.window.analytics.alias(user.UserId, analyticsUser.anonymousId(), {
                        integrations: {
                            All: false,
                            Chameleon: true,
                        },
                    });
                }
            }
            else {
                // We only reset the user id in segment SDK. Anonymous identifies are not necessary. Amplitude doesn't need it, and we're
                // not willing to pay for Chameleon MAUs that includes anonymous users
                analyticsUser === null || analyticsUser === void 0 ? void 0 : analyticsUser.id(null);
            }
        });
    }
    reset() {
        if (this.disabled || !this.window.analytics) {
            return;
        }
        this.window.analytics.reset();
        if (this.window.analytics.user) {
            this.window.analytics.user().id(null);
        }
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    page(pageName, params = {}) {
        if (this.disabled || !this.window.analytics) {
            return;
        }
        this.window.analytics.page(pageName, params);
    }
    /**
     * Method to handle transforming Amplitude event properties. In amplitude, you can decide to
     * transform an event property to lowercase, uppercase, or join array elements
     */
    transform(method, value) {
        let result = value;
        switch (method) {
            case TransformMethods.JOIN:
                result = Array.isArray(result) ? result.join(', ') : result;
                break;
            case TransformMethods.LOWERCASE:
                result = Array.isArray(result) ? result.map((r) => r.toLowerCase()) : result.toLowerCase();
                break;
            case TransformMethods.UPPERCASE:
                result = Array.isArray(result) ? result.map((r) => r.toUpperCase()) : result.toUpperCase();
                break;
        }
        return result;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    track(name, category, params = {}, { gaLabel, gaValue, } = {}) {
        if (this.disabled || !this.window.analytics) {
            return Promise.resolve();
        }
        const segmentParams = Object.assign({}, params);
        // Add analytics for GA. Segment expects a special param `label` or `value` so it can transfer it back to GA
        if (gaLabel) {
            const propertyValue = params[gaLabel];
            segmentParams['label'] = Array.isArray(propertyValue)
                ? this.transform(TransformMethods.JOIN, propertyValue)
                : propertyValue;
        }
        if (gaValue) {
            segmentParams['value'] = params[gaValue];
        }
        return new Promise((resolve) => {
            this.window.analytics.track(name, Object.assign({ 'Event Category': category }, segmentParams), {}, resolve);
        });
    }
}

export { AbstractSegmentService, TransformMethods };
